import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes('"') || text.includes("\n")) {
      return `"${text.replaceAll('"', '""')}"`;
    }
    return text;
  };
  const lines = [headers.join(",")];
  rows.forEach((row) => lines.push(headers.map((h) => escape(row[h])).join(",")));
  return lines.join("\n");
}

function getBookingTs(booking) {
  if (booking?.paidAt?.seconds) return booking.paidAt.seconds * 1000;
  if (booking?.createdAt?.seconds) return booking.createdAt.seconds * 1000;
  return 0;
}

function maskEmail(email) {
  const text = String(email || "").trim();
  if (!text.includes("@")) return "Customer";
  const [local, domain] = text.split("@");
  if (!local || !domain) return "Customer";
  const visible = local.slice(0, 2);
  const hidden = "*".repeat(Math.max(2, local.length - 2));
  return `${visible}${hidden}@${domain}`;
}

function formatAmount(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toneForStatus(status) {
  const value = String(status || "").toLowerCase();
  if (value === "completed") return "success";
  if (value === "accepted" || value === "in_progress") return "accent";
  if (value === "cancelled" || value === "rejected") return "danger";
  return "warning";
}

export default function ProviderEarnings({ user }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const providerId = String(user?.uid || "").trim();
  const providerEmail = String(user?.email || "").trim();
  const providerEmailVariants = useMemo(
    () => [...new Set([providerEmail, providerEmail.toLowerCase()].filter(Boolean))],
    [providerEmail],
  );

  const fetchEarnings = useCallback(async () => {
    if (!providerId && !providerEmailVariants.length) {
      setBookings([]);
      return;
    }
    setLoading(true);
    try {
      setLoadError("");
      const queries = [];
      if (providerId) {
        queries.push(getDocs(query(collection(db, "bookings"), where("providerId", "==", providerId))));
      }
      providerEmailVariants.forEach((emailVariant) => {
        queries.push(getDocs(query(collection(db, "bookings"), where("providerEmail", "==", emailVariant))));
      });
      const snapshots = await Promise.all(queries);
      const merged = new Map();
      snapshots.forEach((snap) => {
        snap.docs.forEach((item) => {
          merged.set(item.id, { id: item.id, ...item.data() });
        });
      });
      setBookings([...merged.values()]);
    } catch (error) {
      setLoadError(error.message || "Unable to load earnings.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [providerId, providerEmailVariants]);

  useEffect(() => {
    if (!providerId && !providerEmailVariants.length) {
      setBookings([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError("");
    const sourceConfigs = [];
    if (providerId) {
      sourceConfigs.push({
        key: `providerId:${providerId}`,
        ref: query(collection(db, "bookings"), where("providerId", "==", providerId)),
      });
    }
    providerEmailVariants.forEach((emailVariant) => {
      sourceConfigs.push({
        key: `providerEmail:${emailVariant}`,
        ref: query(collection(db, "bookings"), where("providerEmail", "==", emailVariant)),
      });
    });

    const bucketBySource = new Map(sourceConfigs.map((item) => [item.key, new Map()]));
    const readyBySource = new Map(sourceConfigs.map((item) => [item.key, false]));

    const applyRows = () => {
      const merged = new Map();
      bucketBySource.forEach((bucket) => {
        bucket.forEach((row, rowId) => merged.set(rowId, row));
      });
      setBookings([...merged.values()]);
    };

    const finishLoadingIfReady = () => {
      if ([...readyBySource.values()].every(Boolean)) {
        setLoading(false);
      }
    };

    const unsubscribers = sourceConfigs.map((config) =>
      onSnapshot(
        config.ref,
        (snapshot) => {
          const bucket = bucketBySource.get(config.key);
          if (!bucket) return;
          bucket.clear();
          snapshot.docs.forEach((item) => {
            bucket.set(item.id, { id: item.id, ...item.data() });
          });
          readyBySource.set(config.key, true);
          applyRows();
          setLoadError("");
          finishLoadingIfReady();
        },
        (error) => {
          readyBySource.set(config.key, true);
          setLoadError(error.message || "Unable to load earnings.");
          applyRows();
          finishLoadingIfReady();
        },
      ),
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [providerId, providerEmailVariants]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const periodDays = periodFilter === "7d" ? 7 : periodFilter === "30d" ? 30 : periodFilter === "90d" ? 90 : 0;
    const fromTs = periodDays ? now - periodDays * 24 * 60 * 60 * 1000 : 0;

    return bookings
      .filter((booking) => booking.paymentStatus === "paid")
      .filter((booking) => (statusFilter ? booking.status === statusFilter : true))
      .filter((booking) => (cityFilter ? String(booking.city || "").toLowerCase().includes(cityFilter.toLowerCase()) : true))
      .filter((booking) => {
        if (!fromTs) return true;
        const ts = getBookingTs(booking);
        return ts ? ts >= fromTs : false;
      });
  }, [bookings, cityFilter, statusFilter, periodFilter]);

  const summary = useMemo(() => {
    const paid = filtered.filter((booking) => booking.status !== "rejected");
    const settled = paid.filter((booking) => booking.refundStatus !== "refund_processed");
    const refundedAmount = paid
      .filter((booking) => booking.refundStatus === "refund_processed")
      .reduce((sum, booking) => sum + (Number(booking.amount) || 0), 0);
    const grossCollected = paid.reduce((sum, booking) => sum + (Number(booking.amount) || 0), 0);
    const gross = settled.reduce((sum, booking) => sum + (Number(booking.amount) || 0), 0);
    const commission = settled.reduce(
      (sum, booking) => sum + (Number(booking.commissionAmount) || (Number(booking.amount) || 0) * 0.2),
      0,
    );
    const net = settled.reduce(
      (sum, booking) => sum + (Number(booking.providerEarning) || (Number(booking.amount) || 0) * 0.8),
      0,
    );
    const acceptedCount = paid.filter((booking) => ["accepted", "in_progress", "completed"].includes(String(booking.status || ""))).length;
    const completedCount = paid.filter((booking) => String(booking.status || "") === "completed").length;
    const refunded = paid.filter((booking) => booking.refundStatus === "refund_processed").length;
    const acceptanceRate = paid.length ? (acceptedCount / paid.length) * 100 : 0;
    const completionRate = paid.length ? (completedCount / paid.length) * 100 : 0;
    const avgTicket = settled.length ? gross / settled.length : 0;
    return {
      gross,
      grossCollected,
      refundedAmount,
      commission,
      net,
      acceptedCount,
      completedCount,
      acceptanceRate,
      completionRate,
      avgTicket,
      count: settled.length,
      refunded,
    };
  }, [filtered]);

  const monthlyRows = useMemo(() => {
    const map = new Map();
    filtered.forEach((booking) => {
      const base = getBookingTs(booking) || Date.now();
      const date = new Date(base);
      const month = date.toLocaleString("en-IN", { month: "short", year: "numeric" });
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const sortTs = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
      const amount = Number(booking.amount) || 0;
      const commission = Number(booking.commissionAmount) || amount * 0.2;
      const net = Number(booking.providerEarning) || amount * 0.8;
      const current = map.get(monthKey) || { month, sortTs, gross: 0, commission: 0, net: 0, count: 0 };
      current.gross += amount;
      current.commission += commission;
      current.net += net;
      current.count += 1;
      map.set(monthKey, current);
    });
    return Array.from(map.values()).sort((a, b) => b.sortTs - a.sortTs).slice(0, 6);
  }, [filtered]);

  const rows = filtered.sort((a, b) => getBookingTs(b) - getBookingTs(a)).slice(0, 20);

  const exportCsv = () => {
    const data = filtered.map((booking) => {
      const amount = Number(booking.amount) || 0;
      const commission = Number(booking.commissionAmount) || amount * 0.2;
      const net = Number(booking.providerEarning) || amount * 0.8;
      return {
        bookingId: booking.id,
        service: booking.serviceTitle || booking.serviceName || "Service",
        city: booking.city || "",
        amount,
        commission,
        net,
        paymentStatus: booking.paymentStatus || "",
        status: booking.status || "",
        bookingDate: booking.bookingDate || "",
        paidAt: getBookingTs(booking) ? new Date(getBookingTs(booking)).toLocaleString("en-IN") : "",
      };
    });
    const csv = toCsv(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `provider-earnings-${Date.now()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Finance"
        title="Earnings Tracker"
        subtitle="Monitor settled income, refund exposure, monthly performance, and booking-level earnings in one place."
        actions={(
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="petaar-button petaar-button--secondary" onClick={exportCsv}>
              Export CSV
            </button>
            <button className="petaar-button" onClick={fetchEarnings} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        )}
      />

      {loadError ? <div className="petaar-notice error" style={{ marginBottom: 16 }}>{loadError}</div> : null}

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Gross collected" value={summary.grossCollected} prefix="₹" decimals={2} tone="default" meta="All paid bookings" />
        <StatCard label="Net earnings" value={summary.net} prefix="₹" decimals={2} tone="success" meta="After 20% platform fee" />
        <StatCard label="Acceptance rate" value={summary.acceptanceRate} suffix="%" decimals={1} tone="accent" meta={`${summary.acceptedCount} accepted jobs`} />
        <StatCard label="Completion rate" value={summary.completionRate} suffix="%" decimals={1} tone="warning" meta={`${summary.completedCount} completed jobs`} />
      </div>

      <section className="petaar-card" style={{ marginBottom: 24 }}>
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Filters</div>
            <h3 style={{ fontSize: "1.35rem" }}>Refine earnings data</h3>
          </div>
          <button
            className="petaar-button petaar-button--ghost"
            onClick={() => {
              setStatusFilter("");
              setCityFilter("");
              setPeriodFilter("all");
            }}
          >
            Clear Filters
          </button>
        </div>
        <div className="petaar-form-grid">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="pending">pending</option>
            <option value="accepted">accepted</option>
            <option value="in_progress">in_progress</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
          </select>
          <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
            <option value="all">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <input value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} placeholder="Filter by city" />
        </div>
      </section>

      <div className="petaar-split" style={{ alignItems: "start", marginBottom: 24 }}>
        <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
          <div className="petaar-kicker">Financial Summary</div>
          <div style={{ display: "grid", gap: 12 }}>
            <div className="petaar-list-row" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Refunded amount</span><strong>{formatAmount(summary.refundedAmount)}</strong></div>
            </div>
            <div className="petaar-list-row" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Settled gross</span><strong>{formatAmount(summary.gross)}</strong></div>
            </div>
            <div className="petaar-list-row" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Platform fee</span><strong>{formatAmount(summary.commission)}</strong></div>
            </div>
            <div className="petaar-list-row" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Average ticket size</span><strong>{formatAmount(summary.avgTicket)}</strong></div>
            </div>
          </div>
        </section>

        <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
          <div className="petaar-kicker">Monthly Trend</div>
          <h3 style={{ fontSize: "1.35rem" }}>Last 6 months</h3>
          {monthlyRows.length === 0 ? (
            <EmptyState
              icon="📉"
              title="No earnings data yet"
              description="Paid bookings will appear here as soon as customers complete payment."
            />
          ) : (
            monthlyRows.map((month) => (
              <div key={month.month} className="petaar-list-row" style={{ padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <strong>{month.month}</strong>
                  <ToneBadge tone="default">{month.count} jobs</ToneBadge>
                </div>
                <div style={{ marginTop: 10, display: "grid", gap: 8, color: "var(--muted)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Gross</span><strong style={{ color: "var(--text)" }}>{formatAmount(month.gross)}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Commission</span><strong style={{ color: "var(--text)" }}>{formatAmount(month.commission)}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Net</span><strong style={{ color: "var(--text)" }}>{formatAmount(month.net)}</strong></div>
                </div>
              </div>
            ))
          )}
        </section>
      </div>

      <section className="petaar-card">
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Recent bookings</div>
            <h3 style={{ fontSize: "1.35rem" }}>Latest paid jobs</h3>
          </div>
          <ToneBadge tone="accent">{rows.length} visible row(s)</ToneBadge>
        </div>

        {loading ? (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={`earnings-skeleton-${index}`} className="petaar-skeleton" style={{ height: 180, borderRadius: 18 }} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon="💼"
            title="No paid bookings yet"
            description="Once customers pay for your services, each settled booking will show its gross amount, commission, and net earning here."
          />
        ) : (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {rows.map((booking) => {
              const amount = Number(booking.amount) || 0;
              const commission = Number(booking.commissionAmount) || amount * 0.2;
              const net = Number(booking.providerEarning) || amount * 0.8;
              return (
                <div key={booking.id} className="petaar-list-row">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <h4 style={{ fontSize: "1.15rem" }}>{booking.serviceTitle || booking.serviceName || "Service"}</h4>
                      <div style={{ marginTop: 6, color: "var(--muted)" }}>
                        {maskEmail(booking.ownerEmail || booking.userEmail || "")} · {booking.city || "City unavailable"}
                      </div>
                    </div>
                    <ToneBadge tone={toneForStatus(booking.status)}>{booking.status || "pending"}</ToneBadge>
                  </div>
                  <div style={{ marginTop: 12, display: "grid", gap: 8, color: "var(--muted)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Gross</span><strong style={{ color: "var(--text)" }}>{formatAmount(amount)}</strong></div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Commission</span><strong style={{ color: "var(--text)" }}>{formatAmount(commission)}</strong></div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Net</span><strong style={{ color: "var(--text)" }}>{formatAmount(net)}</strong></div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Booking date</span><strong style={{ color: "var(--text)" }}>{booking.bookingDate || "N/A"}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
